import type { MouseEvent } from 'react';
import { List, ListItem } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import type { Integration } from '@/services/db/integration/entity';
import { linkClickedProperties } from '../analytics';
import { parseUseCases } from '../use-cases';

type UseCaseListProps = {
  integration: Integration;
};

/**
 * The integration's use-case bullets.
 *
 * The bullets are catalog markup, so a click is tracked by inspecting what was
 * hit rather than by binding a handler per link: only an anchor reports, and it
 * reports the URL it points at.
 */
export function UseCaseList({ integration }: UseCaseListProps) {
  const { track } = useAnalytics();
  const useCases = parseUseCases(integration.use_cases);

  if (useCases.length === 0) {
    return null;
  }

  const onClick = (event: MouseEvent<HTMLElement>) => {
    const anchor = event.target instanceof Element ? event.target.closest('a') : null;

    if (anchor == null) {
      return;
    }

    void track('integration', 'integrationUseCaseLinkClicked', {
      ...linkClickedProperties(integration, anchor.getAttribute('href') ?? ''),
    });
  };

  return (
    <List mb="250" gap="100">
      {useCases.map((useCase, index) => (
        <ListItem
          // The bullets are markup with no id of their own, and the list is static.
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          onClick={onClick}
          // oxlint-disable-next-line react/no-danger -- sanitized above by parseUseCases
          // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
          dangerouslySetInnerHTML={{ __html: useCase }}
        />
      ))}
    </List>
  );
}
